import { useEffect, useRef, useState, useCallback } from 'react';
import isNil from 'lodash/isNil';

// Hook
// export const useHover = (color = '#eaeaea') => {
//     const [fade, setFade] = useState(false);

//     const onMouseEnter = () => {
//         setFade(true);
//     };

//     const onMouseLeave = () => {
//         setFade(false);
//     };

//     const hoverStyle = !fade ? {
//         backgroundColor: null, transition: 'all .2s ease-in-out',
//     } : {
//             backgroundColor: color, transition: 'all .2s ease-in-out',
//         };

//     return { hoverStyle, onMouseEnter, onMouseLeave };
// };

export const useHover = () => {
    const [value, setValue] = useState(false);

    // Wrap in useCallback so we can use in dependencies below
    const handleMouseOver = useCallback(() => setValue(true), []);
    const handleMouseOut = useCallback(() => {
        return setValue(false);
    }, []);

    // Keep track of the last node passed to callbackRef
    // so we can remove its event listeners.
    const ref = useRef();

    // Use a callback ref instead of useEffect so that event listeners
    // get changed in the case that the returned ref gets added to
    // a different element later. With useEffect, changes to ref.current
    // wouldn't cause a rerender and thus the effect would run again.
    const callbackRef = useCallback(
        node => {
            if (ref.current) {
                ref.current.removeEventListener("mouseenter", handleMouseOver);
                ref.current.removeEventListener("mouseleave", handleMouseOut);
            }

            ref.current = node;

            if (ref.current) {
                ref.current.addEventListener("mouseenter", handleMouseOver);
                ref.current.addEventListener("mouseleave", handleMouseOut);
            }
        },
        [handleMouseOver, handleMouseOut]
    );

    return [callbackRef, value];
}

// Hook
export function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// Hook
export const usePrevious = (value) => {
    // The ref object is a generic container whose current property is mutable ...
    // ... and can hold any value, similar to an instance property on a class
    const ref = useRef();

    // Store current value in ref
    useEffect(() => {
        ref.current = value;
    }, [value]); // Only re-run if value changes

    // Return previous value (happens before update in useEffect above)
    return ref.current;
}

// Hook
export const useLocalStorage = (key, initialValue) => {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
        try {
            // Get from local storage by key
            const item = !isNil(key) ? window.localStorage.getItem(key) : null;
            // Parse stored json or if none return initialValue
            return !isNil(item) ? JSON.parse(item) : initialValue;
        } catch (error) {
            // If error also return initialValue
            console.error(error);
            return initialValue;
        }
    });

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = value => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            // Save state
            setStoredValue(valueToStore);
            if (!isNil(key)) {
                // Save to local storage
                window.localStorage.setItem(key, JSON.stringify(valueToStore));
            }
        } catch (error) {
            // A more advanced implementation would handle the error case
            console.log(error);
        }
    };

    return [storedValue, setValue];
}

//
export const useConfirmDialog = (props) => {
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        msg: null,
        tipo: null,
        payload: null,
        okLabel: null,
        cancelLabel: null,
        ...props,
    });
    return [confirmDialog, setConfirmDialog];
}

// Hook
// export const useUnmounted = () => {
//     const unmounted = useRef(false)
//     useEffect(() => () => {
//         unmounted.current = true
//     }, [])
//     return unmounted
// }

export const useIsMounted = () => {
    const isMounted = useRef(false)
    useEffect(() => {
        isMounted.current = true
        return function cleanup() {
            isMounted.current = false
        }
    }, [])
    const checker = useCallback(() => {
        return isMounted.current
    }, [])
    return checker
}

export const useCancel = (source) => {
    useEffect(() => {
        return () => {
            if (source) {
                source.cancel();
            }
        };
    }, [source]);
}

const initBeforeUnLoad = (showExitPrompt) => {
    window.onbeforeunload = (event) => {
        if (showExitPrompt) {
            const e = event || window.event;
            e.preventDefault();
            if (e) {
                e.returnValue = '';
            }
            return '';
        }
    };
};

// Hook
export const useExitPrompt = (bool) => {
    const [showExitPrompt, setShowExitPrompt] = useState(bool);

    window.onload = function () {
        initBeforeUnLoad(showExitPrompt);
    };

    useEffect(() => {
        initBeforeUnLoad(showExitPrompt);
        return () => {
            window.onbeforeunload = null;
        }
    }, [showExitPrompt]);

    return [showExitPrompt, setShowExitPrompt];
}


export const useCopyToClipboard = () => {
    const [copiedText, setCopiedText] = useState(null)

    const copy = async text => {
        if (!navigator?.clipboard) {
            console.warn('Clipboard not supported')
            return false
        }

        // Try to save to clipboard then save it in the state if worked
        try {
            await navigator.clipboard.writeText(text)
            setCopiedText(text)
            return true
        } catch (error) {
            console.warn('Copy failed', error)
            setCopiedText(null)
            return false
        }
    }

    return [copiedText, copy]
}
